/**
 * 슬래시 명령어 팝업 컴포넌트
 * @description 에디터에서 / 입력 시 나타나는 태그 선택 팝업
 */

import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Tag01Icon } from "@hugeicons/core-free-icons";
import { cn } from "@/lib/utils";
import type { PlaceholderConfig } from "../types/sms.types";

interface SlashCommandPopupProps {
  /** 팝업 표시 여부 */
  open: boolean;
  /** 팝업 닫기 핸들러 */
  onClose: () => void;
  /** 사용 가능한 플레이스홀더 목록 */
  placeholders: PlaceholderConfig[];
  /** 태그 선택 핸들러 */
  onSelect: (placeholder: PlaceholderConfig) => void;
  /** 팝업 위치 (top, left) */
  position?: { top: number; left: number };
  /** 추가 CSS 클래스 */
  className?: string;
}

/** 카테고리별로 플레이스홀더 그룹화 */
function groupByCategory(
  placeholders: PlaceholderConfig[],
): Record<string, PlaceholderConfig[]> {
  return placeholders.reduce(
    (acc, config) => {
      const category = config.category || "기타";
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(config);
      return acc;
    },
    {} as Record<string, PlaceholderConfig[]>,
  );
}

const CATEGORY_ORDER = ["배차업체", "주문", "상차지", "하차지", "화물", "시스템", "기타"];

interface TagItemProps {
  config: PlaceholderConfig;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  dataIndex: number;
}

function TagItem({
  config,
  isSelected,
  onClick,
  onMouseEnter,
  dataIndex,
}: TagItemProps) {
  return (
    <button
      type="button"
      data-index={dataIndex}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={cn(
        "w-full flex items-center justify-between px-2 py-1.5 rounded text-left",
        "transition-colors cursor-pointer",
        isSelected ? "bg-accent text-accent-foreground" : "hover:bg-muted",
      )}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span
          className="px-1.5 py-0.5 rounded text-[10px] font-medium text-white shrink-0"
          style={{ backgroundColor: config.color }}
        >
          #
        </span>
        <span className="text-xs font-medium truncate">{config.label}</span>
      </div>
      {config.description && (
        <span className="text-[10px] text-muted-foreground truncate max-w-[140px] ml-2">
          {config.description}
        </span>
      )}
    </button>
  );
}

export function SlashCommandPopup({
  open,
  onClose,
  placeholders,
  onSelect,
  position,
  className,
}: SlashCommandPopupProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const grouped = useMemo(() => groupByCategory(placeholders), [placeholders]);

  const sortedCategories = useMemo(() => {
    return Object.keys(grouped).sort((a, b) => {
      const indexA = CATEGORY_ORDER.indexOf(a);
      const indexB = CATEGORY_ORDER.indexOf(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }, [grouped]);

  const flatList = useMemo(() => {
    return sortedCategories.flatMap((category) => grouped[category]);
  }, [sortedCategories, grouped]);

  const selectedItem = flatList[selectedIndex];

  // 팝업 열릴 때 인덱스 초기화
  const [prevOpen, setPrevOpen] = useState(open);
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (open) {
      setSelectedIndex(0);
    }
  }

  // 선택된 항목으로 자동 스크롤
  useEffect(() => {
    if (!open) return;

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const selectedElement = scrollContainer.querySelector(
      `[data-index="${selectedIndex}"]`,
    ) as HTMLElement | null;

    if (selectedElement) {
      selectedElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [open, selectedIndex]);

  const handleSelect = useCallback(
    (config: PlaceholderConfig) => {
      onSelect(config);
      onClose();
    },
    [onSelect, onClose],
  );

  // 키보드 이벤트 핸들러
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation();
          setSelectedIndex((prev) => (prev + 1) % flatList.length);
          break;
        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          setSelectedIndex(
            (prev) => (prev - 1 + flatList.length) % flatList.length,
          );
          break;
        case "Enter":
          e.preventDefault();
          e.stopPropagation();
          if (selectedItem) {
            handleSelect(selectedItem);
          }
          break;
        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          onClose();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [open, flatList.length, selectedItem, handleSelect, onClose]);

  const getItemIndex = useCallback(
    (config: PlaceholderConfig) => {
      return flatList.findIndex(
        (item) => item.placeholder === config.placeholder,
      );
    },
    [flatList],
  );

  if (!open) return null;

  return (
    <div
      ref={containerRef}
      className={cn(
        "absolute z-50 w-[280px] max-h-[280px] bg-popover border rounded-lg shadow-lg",
        "animate-in fade-in-0 zoom-in-95 duration-150",
        "flex flex-col overflow-hidden",
        className,
      )}
      style={
        position
          ? { top: position.top, left: position.left }
          : { top: "100%", left: 0, marginTop: 4 }
      }
    >
      {/* 헤더 */}
      <div className="px-3 py-2 border-b shrink-0">
        <div className="flex items-center gap-2">
          <HugeiconsIcon icon={Tag01Icon} size={14} strokeWidth={2} />
          <span className="text-xs font-medium">변수 삽입</span>
        </div>
      </div>

      {/* 태그 목록 */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto min-h-0 p-1.5 space-y-2"
      >
        {sortedCategories.map((category) => (
          <div key={category}>
            <p className="px-2 py-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
              {category}
            </p>
            <div className="space-y-0.5">
              {grouped[category].map((config) => {
                const itemIndex = getItemIndex(config);
                return (
                  <TagItem
                    key={config.placeholder}
                    config={config}
                    isSelected={selectedIndex === itemIndex}
                    onClick={() => handleSelect(config)}
                    onMouseEnter={() => setSelectedIndex(itemIndex)}
                    dataIndex={itemIndex}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* 푸터 힌트 */}
      <div className="px-3 py-1.5 border-t bg-muted/30 shrink-0">
        <div className="flex items-center justify-between text-[10px] text-muted-foreground">
          <span>↑↓ 이동</span>
          <span>Enter 선택</span>
          <span>Esc 닫기</span>
        </div>
      </div>
    </div>
  );
}

export default SlashCommandPopup;
